import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  HelpOutline,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  FileCopy as FileCopyIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Flag as FlagIcon,
  Visibility as VisibilityIcon,
  Group as GroupIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";

const DuplicateWorkflowUserGuide = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "90vh",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
          color: theme.palette.info.contrastText,
          fontWeight: 700,
          fontSize: "1.5rem",
          py: 3,
          px: 4,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: `linear-gradient(45deg, ${theme.palette.info.light}, ${theme.palette.info.main})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: theme.shadows[3],
            }}
          >
            <HelpOutline
              sx={{ color: theme.palette.info.contrastText, fontSize: 24 }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("duplicateWorkflow.userGuideTitle")}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t("duplicateWorkflow.userGuideSubtitle")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.info.contrastText,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ maxHeight: "70vh", overflowY: "auto", p: 4 }}>
          {/* Overview Section */}
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              color="info.main"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {t("duplicateWorkflow.guideScope")}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {t("duplicateWorkflow.guideScopeText")}
            </Typography>
          </Box>

          {/* Section 1: Open Workflow */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <VisibilityIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideSection1Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep1_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep1_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep1_3")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep1_4")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Name and Target */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideSection2Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep2_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep2_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep2_3")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  mt: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("duplicateWorkflow.guideTargetOptions")}
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CategoryIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <strong>
                            {t("duplicateWorkflow.guideWorkPermit")}
                          </strong>{" "}
                          {t("duplicateWorkflow.guideWorkPermitText")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CategoryIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <strong>
                            {t("duplicateWorkflow.guideObservation")}
                          </strong>{" "}
                          {t("duplicateWorkflow.guideObservationText")}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Box>

              <Box
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  borderRadius: 1,
                  p: 2,
                  mt: 2,
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <WarningIcon color="warning" />
                <Box>
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ fontWeight: 600 }}
                  >
                    {t("duplicateWorkflow.guideWarning")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("duplicateWorkflow.guideWarningText")}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Add New Step */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AddIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideSection3Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep3_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep3_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep3_3")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep3_4")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("duplicateWorkflow.guideStepFields")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        height: "100%",
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <GroupIcon color="primary" fontSize="small" />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                          {t("duplicateWorkflow.guideAuthorizedGroups")} *
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("duplicateWorkflow.guideAuthorizedGroupsText")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        height: "100%",
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <EditIcon color="primary" fontSize="small" />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                          {t("duplicateWorkflow.guideActionName")} *
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("duplicateWorkflow.guideActionNameText")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 4: Edit Existing Step */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideSection4Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep4_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep4_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep4_3")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep4_4")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 5: Delete Step */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DeleteIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideSection5Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep5_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep5_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep5_3")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>

              <Box
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  borderRadius: 1,
                  p: 2,
                  mt: 2,
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <WarningIcon color="warning" />
                <Box>
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ fontWeight: 600 }}
                  >
                    {t("duplicateWorkflow.guideNote")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("duplicateWorkflow.guideDeleteNote")}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 6: Manage Step Inputs */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AssignmentIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideSection6Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {t("duplicateWorkflow.guideSection6Text")}
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep6_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep6_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep6_3")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep6_4")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep6_5")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 7: Save Workflow */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <SaveIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideSection7Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep7_1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep7_2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideStep7_3")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("duplicateWorkflow.guideStatusIndicator")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "success.main",
                          }}
                        />
                        {t("duplicateWorkflow.guideGreenDot")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "warning.main",
                          }}
                        />
                        {t("duplicateWorkflow.guideYellowDot")}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 8: Example */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideExampleTitle")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  borderRadius: 2,
                }}
              >
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <FileCopyIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {t("duplicateWorkflow.guideExampleStep1")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EditIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {t("duplicateWorkflow.guideExampleStep2")}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SaveIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {t("duplicateWorkflow.guideExampleStep3")}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Key Points */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 2,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.12) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FlagIcon color="warning" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("duplicateWorkflow.guideKeyPointsTitle")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideKeyPoint1")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideKeyPoint2")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideKeyPoint3")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideKeyPoint4")}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {t("duplicateWorkflow.guideKeyPoint5")}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Completion */}
          <Box
            sx={{
              p: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              🎉 {t("duplicateWorkflow.guideCompletionTitle")}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {t("duplicateWorkflow.guideCompletionText")}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.05) }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          color="info"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("userGuide.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DuplicateWorkflowUserGuide.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DuplicateWorkflowUserGuide;
